import {
  AUTH0_ORGANIZATION_ID_CLAIM,
  FAMILY_NAME_CLAIM,
  GIVEN_NAME_CLAIM,
  IF_CLIENT_ID_CLAIM,
  IF_EXTERNAL_USER_ID_CLAIM,
  getNamespacedClaim,
  parseTokenExpiry,
  parseTokenPayload,
} from './token-utils';
import type { JwtPayload } from './types';

type TokenPair = {
  accessToken: string | null;
  idToken: string | null;
};

type ClaimNames = string | readonly string[];

export type AuthSessionTokenClaims = {
  tokenExpiresAt: number | null;
  ifExternalUserId: string | null;
  ifClientId: string | null;
  givenName: string | null;
  familyName: string | null;
  organizationId: string | null;
};

export const parseNullableTokenPayload = (token: string | null): JwtPayload | null => {
  return token ? parseTokenPayload(token) : null;
};

const getClaimNames = (claimNames: ClaimNames): readonly string[] => {
  return typeof claimNames === 'string' ? [claimNames] : claimNames;
};

const getBooleanPayloadClaim = (
  payload: JwtPayload | null,
  claimNames: ClaimNames,
): boolean | null => {
  if (!payload) {
    return null;
  }

  for (const claimName of getClaimNames(claimNames)) {
    const directValue = payload[claimName];

    if (typeof directValue === 'boolean') {
      return directValue;
    }

    const namespacedEntry = Object.entries(payload).find(
      ([key, value]) => key.endsWith(`/${claimName}`) && typeof value === 'boolean',
    );

    if (typeof namespacedEntry?.[1] === 'boolean') {
      return namespacedEntry[1];
    }
  }

  return null;
};

const resolveBasicInfoClaim = ({
  accessPayload,
  idPayload,
  claimName,
}: {
  accessPayload: JwtPayload | null;
  idPayload: JwtPayload | null;
  claimName: string;
}) => {
  return getNamespacedClaim(idPayload, claimName) ?? getNamespacedClaim(accessPayload, claimName);
};

export const getBooleanTokenClaim = (
  token: string | null,
  claimNames: ClaimNames,
): boolean | null => {
  return getBooleanPayloadClaim(parseNullableTokenPayload(token), claimNames);
};

export const getNamespacedStringTokenClaim = (
  token: string | null,
  claimName: string,
): string | null => {
  return getNamespacedClaim(parseNullableTokenPayload(token), claimName);
};

export const getDirectStringTokenClaim = (
  token: string | null,
  claimName: string,
): string | null => {
  const value = parseNullableTokenPayload(token)?.[claimName];

  return typeof value === 'string' && value.length > 0 ? value : null;
};

export const getTokenOrganizationId = (token: string | null): string | null => {
  return getNamespacedStringTokenClaim(token, AUTH0_ORGANIZATION_ID_CLAIM);
};

export const resolveAuthSessionTokenClaims = ({
  accessToken,
  idToken,
}: TokenPair): AuthSessionTokenClaims => {
  const accessPayload = parseNullableTokenPayload(accessToken);
  const idPayload = parseNullableTokenPayload(idToken);

  return {
    tokenExpiresAt: parseTokenExpiry(accessPayload),
    ifExternalUserId: getNamespacedClaim(accessPayload, IF_EXTERNAL_USER_ID_CLAIM),
    ifClientId: getNamespacedClaim(accessPayload, IF_CLIENT_ID_CLAIM),
    givenName: resolveBasicInfoClaim({
      accessPayload,
      idPayload,
      claimName: GIVEN_NAME_CLAIM,
    }),
    familyName: resolveBasicInfoClaim({
      accessPayload,
      idPayload,
      claimName: FAMILY_NAME_CLAIM,
    }),
    organizationId: getNamespacedClaim(idPayload, AUTH0_ORGANIZATION_ID_CLAIM),
  };
};
